import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { SecureAuth } from "asset/icons";
import ErrorMessage from "../ErrorMessage";
import Loader from "component/loader";
import { COMPLETE_LOGIN } from "store/action";
import { checkAutoLogin } from "store/authServices";
import BackupVerificationModal from "../2FA/BackupVerificationModal";

const Confirm2FAModal = ({
    isOpen,
    onClose,
    onSuccess,
    onUseBackup,
    title = "Verify your identity",
    description = "Enter the 6-digit code from your authenticator app to continue.",
    submitLabel = "Verify",
    verifyButtonLabel,
    onVerifyCode,
    emptyMessage = "Please enter the 6-digit code from your authenticator app.",
    errorFallback = "Invalid authentication code. Please try again.",
    backLabel = "Back",
    helpLabel = "Can't access codes?",
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [code, setCode] = useState(["", "", "", "", "", ""]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showBackupModal, setShowBackupModal] = useState(false);
    const inputRefs = useRef([]);

    useEffect(() => {
        if (!isOpen) return;

        setCode(["", "", "", "", "", ""]);
        setError("");
        setShowBackupModal(false);

        if (typeof onVerifyCode === "function") return;

        const pendingLogin = sessionStorage.getItem("pendingLogin");
        if (!pendingLogin) {
            navigate("/auth/signin", { replace: true });
        }
    }, [isOpen, navigate, onVerifyCode]);

    if (!isOpen) return null;

    const handleChange = (index, value) => {
        if (/^[0-9]?$/.test(value)) {
            const newCode = [...code];
            newCode[index] = value;
            setCode(newCode);
            setError("");

            if (value && index < code.length - 1) {
                inputRefs.current[index + 1]?.focus();
            }
        }
    };

    const handleKeyDown = (index, event) => {
        if (event.key === "Backspace" && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const finishLogin = async (verifiedLoginData) => {
        COMPLETE_LOGIN(verifiedLoginData, dispatch);
        sessionStorage.removeItem("pendingLogin");
        await checkAutoLogin(dispatch);

        if (typeof onSuccess === "function") {
            onSuccess(verifiedLoginData);
            return;
        }

        if (verifiedLoginData?.user?.pinStatus === "NOT_VERIFIED") {
            navigate("/auth/setup-pin", { replace: true });
            return;
        }

        const lastVisitedPath = sessionStorage.getItem("lastVisitedPath");
        if (lastVisitedPath && lastVisitedPath !== "/" && lastVisitedPath !== "/auth/signin") {
            sessionStorage.removeItem("lastVisitedPath");
            navigate(lastVisitedPath, { replace: true });
            return;
        }

        navigate("/dashboard", { replace: true });
    };

    const handleVerify = async () => {
        const joinedCode = code.join("");
        if (joinedCode.length !== 6) {
            setError(emptyMessage);
            return;
        }

        try {
            setLoading(true);
            setError("");

            if (typeof onVerifyCode === "function") {
                const result = await onVerifyCode(joinedCode);
                setCode(["", "", "", "", "", ""]);

                if (typeof onSuccess === "function") {
                    onSuccess(result);
                } else if (typeof onClose === "function") {
                    onClose();
                }

                return;
            }

            const pendingLoginString = sessionStorage.getItem("pendingLogin");
            if (!pendingLoginString) {
                setError("Your sign-in session expired. Please sign in again.");
                navigate("/auth/signin", { replace: true });
                return;
            }

            const pendingLogin = JSON.parse(pendingLoginString);

            const verifyResponse = await axios.post(
                "/v1/users/mfa-verify",
                { code: joinedCode },
                {
                    headers: {
                        Authorization: `Bearer ${pendingLogin.token}`,
                    },
                }
            );

            const verifiedLoginData = verifyResponse?.data?.data || {};
            if (!verifiedLoginData?.token) {
                throw new Error("Missing permanent token from MFA verification response.");
            }

            await finishLogin(verifiedLoginData);
        } catch (verifyError) {
            setError(verifyError?.response?.data?.message || verifyError?.message || errorFallback);
        } finally {
            setLoading(false);
        }
    };

    const handleUseBackup = () => {
        if (typeof onUseBackup === "function") {
            onUseBackup();
            return;
        }

        if (typeof onVerifyCode === "function") return;

        setShowBackupModal(true);
    };

    const handleBack = () => {
        if (typeof onClose === "function") {
            onClose();
            return;
        }

        navigate(-1);
    };

    return (
        <>
            {!showBackupModal && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black bg-opacity-50 px-4 py-8">
                    <div className="relative w-full max-w-[650px] overflow-hidden pt-10 rounded-xl border border-[#EBEDF9] bg-white shadow-lg">
                        {onClose && (
                            <button
                                type="button"
                                onClick={onClose}
                                className="absolute right-4 top-4 text-4xl text-[#667085]"
                            >
                                ×
                            </button>
                        )}
                        {/* <div className="!pt-5 pl-2">
                            <SecureAuth />
                        </div> */}

                        <div className="px-8">
                            <h2 className="heading-h2-semibold text-center text-[#101828]">
                                {title}
                            </h2>
                            <p className="mt-2 text-center aku-body-normal-regular text-[#6F739B]">
                                {description}
                            </p>

                            <div className="mt-8 flex gap-3 justify-center">
                                {code.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={(element) => (inputRefs.current[index] = element)}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength="1"
                                        value={digit}
                                        placeholder="-"
                                        onChange={(event) =>
                                            handleChange(index, event.target.value)
                                        }
                                        onKeyDown={(event) => handleKeyDown(index, event)}
                                        className="h-16 w-16 rounded-xl bg-[#F2F4F7] text-center text-2xl text-[#344054] outline-none transition placeholder:text-[#667085] focus:border-[#6F00FF] focus:ring-2 focus:ring-[#E9D7FE]"
                                    />
                                ))}
                            </div>

                            <div className="mt-4 flex justify-center">
                                <ErrorMessage message={error} />
                            </div>
                        </div>

                        <div className="flex flex-col gap-4 border-t border-[#EAECF0] px-8 py-6 sm:flex-row sm:items-center sm:justify-between">
                            {typeof onVerifyCode !== "function" ? (
                                <button
                                    type="button"
                                    onClick={handleUseBackup}
                                    className="text-left text-sm font-medium text-[#101828] hover:underline"
                                >
                                    {helpLabel}
                                </button>
                            ) : (
                                <div />
                            )}

                            <div className="flex items-center justify-end gap-8">
                                <button
                                    type="button"
                                    onClick={handleBack}
                                    className="text-sm font-medium text-[#101828] hover:underline"
                                >
                                    {backLabel}
                                </button>

                                <button
                                    type="button"
                                    onClick={handleVerify}
                                    disabled={code.join("").length !== 6 || loading}
                                    className={`rounded-lg px-6 py-3 text-sm font-semibold text-white transition ${
                                        code.join("").length !== 6 || loading
                                            ? "cursor-not-allowed bg-[#CFC4FB]"
                                            : "bg-[#6F00FF] hover:bg-[#6f00fff0]"
                                    }`}
                                >
                                    {loading ? <Loader /> : verifyButtonLabel || submitLabel}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <BackupVerificationModal
                isOpen={showBackupModal}
                onClose={() => setShowBackupModal(false)}
                onBack={() => setShowBackupModal(false)}
            />
        </>
    );
};

export default Confirm2FAModal;
